import { FC, useCallback, useMemo } from 'react';

import { Typography, Chip } from '@mui/material';
import { useIntl } from 'react-intl';

import { InspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto as AccessibleAccount } from 'shared/models/payments/inspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto';

import { StyledAvatar } from '../Avatar';
import { StyledMenuItem } from './MenuItem.styled';
import {
  MENU_ITEM_TEST_ID,
  MENU_ITEM_GRID_AVATAR,
  MENU_ITEM_GRID_NAME,
  MENU_ITEM_GRID_ACCOUNT,
  MENU_ITEM_GRID_STATUS
} from './MenuItem.constants';
import { getInitials } from '../UserSelector.utils';

export interface MenuItemProps extends AccessibleAccount {
  onClick: (account: AccessibleAccount) => void;
}

export const MenuItem: FC<MenuItemProps> = ({ onClick, ...accountProps }) => {
  const {
    accountHolderForename,
    accountHolderSurname,
    accountBillingCode,
    accountHolderEmail,
    isCurrentUser
  } = accountProps;
  const { formatMessage } = useIntl();

  const fullName = `${accountHolderForename} ${accountHolderSurname}`;

  const initials = useMemo(() => getInitials(fullName), [fullName]);

  const handleClick = useCallback(() => {
    onClick(accountProps);
  }, [onClick, accountProps]);

  return (
    <StyledMenuItem
      data-testid={MENU_ITEM_TEST_ID}
      onClick={handleClick}
      isCurrentUser={isCurrentUser}
    >
      <StyledAvatar src={undefined} alt={fullName} sx={{ gridArea: MENU_ITEM_GRID_AVATAR }}>
        {initials}
      </StyledAvatar>

      <Typography
        variant='body2'
        fontWeight='medium'
        noWrap
        sx={{
          gridArea: MENU_ITEM_GRID_NAME
        }}
      >
        {fullName}
      </Typography>
      <Typography
        variant='caption'
        color='text.secondary'
        noWrap
        sx={{
          gridArea: MENU_ITEM_GRID_ACCOUNT
        }}
      >
        {accountBillingCode || accountHolderEmail}
      </Typography>
      {!isCurrentUser && (
        <Chip
          size='small'
          variant='outlined'
          color='secondary'
          sx={{
            gridArea: MENU_ITEM_GRID_STATUS
          }}
          label={formatMessage({ id: 'userSelector.chips.observerView' })}
        />
      )}
    </StyledMenuItem>
  );
};
